import React from 'react';
import { Button, Col, Container, Row, Spinner } from 'reactstrap';
import { TextField } from '@material-ui/core';

import 'antd/dist/antd.css';
import { FaCheckCircle, FaPlusCircle, FaTimesCircle } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { GrupoRow } from 'views/pages/AtaProposta/GruposAta/styles';

export interface ProdutoAssociado {
  label: string;
  value: number;
}

export interface IGrupoEmail {
  id?: number;
  email: string;
  nome: string;
  tipo: string;
  alterado?: boolean;
  produtoId?: number[];
  indicEnviarAta?: boolean;
}

interface GerenciamentoEmailBaseProps {
  tipo: string;
  gruposEmail: IGrupoEmail[];
  setGruposEmail: React.Dispatch<React.SetStateAction<IGrupoEmail[]>>;

  isLoading: boolean;
  criandoItem: boolean;

  criar: (dados: IGrupoEmail) => Promise<void>;
  atualizar: (dados: IGrupoEmail) => Promise<void>;
  excluir: (id: number) => Promise<void>;

  renderCamposEspecificos?: (
    grupoEmail: IGrupoEmail,
    handleSetCadDetalheValue: (
      grupo: IGrupoEmail,
      campo: string,
      valor: any,
    ) => void,
  ) => React.ReactNode;
}

const GerenciamentoEmailBase = ({
  tipo,
  gruposEmail,
  setGruposEmail,
  isLoading,
  criandoItem,
  criar,
  atualizar,
  excluir,
  renderCamposEspecificos,
}: GerenciamentoEmailBaseProps): JSX.Element => {
  const handleAdicionar = (): void => {
    const novoGrupo = {
      id: undefined,
      email: '',
      nome: '',
      tipo,
      alterado: true,
    };

    setGruposEmail(prev => [...prev, novoGrupo]);
  };

  const handleSetCadDetalheValue = (
    grupo: IGrupoEmail,
    campo: string,
    valor: any,
  ): void => {
    setGruposEmail(prev =>
      prev.map(item =>
        item === grupo
          ? {
              ...item,
              [campo]: valor,
              alterado: true,
            }
          : item,
      ),
    );
  };

  const handleSave = async (grupo: IGrupoEmail): Promise<void> => {
    try {
      if (grupo.id) {
        await atualizar(grupo);
      } else {
        await criar(grupo);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const questionaExclusao = async (grupo: IGrupoEmail): Promise<void> => {
    const result = await Swal.fire({
      title: 'Deseja excluir?',
      text: `O usuário "${grupo.nome}" será excluído.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sim, excluir',
      confirmButtonColor: '#d33',
      cancelButtonText: 'Não',
    });

    if (!result.isConfirmed || !grupo.id) {
      return;
    }

    try {
      await excluir(grupo.id);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Container
        className="mt-4"
        fluid
        style={{
          backgroundColor: '#fff',
          border: '1px solid #ebe9e9',
        }}
      >
        <Row className="px-3 py-3 align-items-center">
          <Col>
            <h4 className="mb-0">Gerenciamento de E-mails ({tipo})</h4>
          </Col>

          <Col xs="auto">
            <Button
              disabled={criandoItem || isLoading}
              size="sm"
              className="btn btn-outline-primary"
              type="button"
              onClick={handleAdicionar}
            >
              <FaPlusCircle className="mr-1" />
              <span className="btn-inner--text">Novo</span>
            </Button>
          </Col>
        </Row>

        <hr className="my-0" />

        {isLoading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: 150 }}
          >
            <Spinner color="primary" />
          </div>
        ) : (
          <Col className="py-3">
            {gruposEmail.map(grupoEmail => {
              const { id, email, nome } = grupoEmail;

              return (
                <GrupoRow key={id} onSubmit={() => handleSave(grupoEmail)}>
                  <Col sm="12">
                    <Row className="align-items-center">
                      <Col sm="3">
                        <TextField
                          inputProps={{
                            style: { fontSize: 14 },
                          }}
                          style={{ width: '100%' }}
                          size="small"
                          variant="outlined"
                          label="Email *"
                          value={email ?? ''}
                          onChange={event =>
                            handleSetCadDetalheValue(
                              grupoEmail,
                              'email',
                              event.target.value,
                            )
                          }
                        />
                      </Col>

                      <Col sm="3">
                        <TextField
                          inputProps={{
                            style: { fontSize: 14 },
                          }}
                          style={{ width: '100%' }}
                          size="small"
                          variant="outlined"
                          label="Nome *"
                          value={nome ?? ''}
                          onChange={event =>
                            handleSetCadDetalheValue(
                              grupoEmail,
                              'nome',
                              event.target.value,
                            )
                          }
                        />
                      </Col>

                      {renderCamposEspecificos?.(
                        grupoEmail,
                        handleSetCadDetalheValue,
                      )}

                      <Col
                        xs="auto"
                        className="d-flex align-items-center"
                        style={{
                          paddingLeft: 5,
                          paddingRight: 5,
                        }}
                      >
                        {grupoEmail.alterado && (
                          <Button
                            size="sm"
                            className="btn-icon btn-2"
                            color="primary"
                            type="button"
                            onClick={() => handleSave(grupoEmail)}
                          >
                            <FaCheckCircle />
                          </Button>
                        )}

                        <Button
                          size="sm"
                          className="btn-icon btn-2"
                          color="danger"
                          type="button"
                          onClick={() => questionaExclusao(grupoEmail)}
                        >
                          <FaTimesCircle />
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                </GrupoRow>
              );
            })}
          </Col>
        )}
      </Container>
    </>
  );
};

export default GerenciamentoEmailBase;
